import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { filterEntities } from '@app/Shared/util/filterEntities';
import { AppState } from '@app/Store';
import { loadDefaultColumnConfig, saveDefaultColumnConfig, saveDefaultColumnConfigSuccess } from '@app/Store/actions/column-config-actions';
import { columnConfigFeature, selectColumnConfigForEntity } from '@app/Store/features/column-config.feature';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import { ColumnConfig, resetColumnConfig } from '@app/Store/reducers/column-config.reducer';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  entities: string[] = [];
  selectedEntity: string = '';
  columnConfig: ColumnConfig[] = [];
  originalConfig: ColumnConfig[] = [];
  dragIndex: number = -1;
  dropIndex: number = -1;
  isSaving = false;
  private configSubscription?: Subscription;
  private saveSuccessSubscription?: Subscription;
  private entitiesSubscription?: Subscription;
constructor(
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private actions$: Actions
  ) {}
ngOnInit(): void {
     this.entitiesSubscription = combineLatest([
      this.store.select(dashboardFeatureSelectors.selectEntities),
      this.store.select(dashboardFeatureSelectors.selectMasterTables)
    ]).subscribe(([entities, masterTables]) => {
      // Use filterEntities with empty string to get all filtered entities
      const filteredEntities = filterEntities('', entities, masterTables);
      this.entities = filteredEntities.map(e => e.entityName);
      if (this.entities.length > 0 && !this.selectedEntity) {
        this.selectedEntity = this.entities[0];
        this.loadEntityConfig(this.selectedEntity);
      }
    });
    
    // Listen for save success
    this.saveSuccessSubscription = this.actions$.pipe(ofType(saveDefaultColumnConfigSuccess)).subscribe(() => {
      this.isSaving = false;
      this.cdr.detectChanges();
      this.alertService.notify(AlertType.Success, 'Column settings saved successfully!', 'Success');
    });
  }

  ngOnDestroy(): void {
    if (this.configSubscription) {
      this.configSubscription.unsubscribe();
    }
    if (this.saveSuccessSubscription) {
      this.saveSuccessSubscription.unsubscribe();
    }
    if (this.entitiesSubscription) {
      this.entitiesSubscription.unsubscribe();
    }
  }
loadEntityConfig(entity: string) {
    if (this.configSubscription) {
      this.configSubscription.unsubscribe();
    }
    this.selectedEntity = entity;
    
    // Check if config exists in store, if not load it
    this.store.select(columnConfigFeature.selectColumnConfigState).subscribe(state => {
      if (!state[entity] || state[entity].length === 0) {
        // Config not loaded, dispatch load action
        this.store.dispatch(loadDefaultColumnConfig({ gridId: entity }));
      }
    }).unsubscribe(); // Just check once
    
    // Subscribe to config changes
    this.configSubscription = this.store.select(selectColumnConfigForEntity(entity)).subscribe(cfg => {
      // Deep clone to avoid frozen objects from store
      this.columnConfig = cfg && cfg.length > 0 ? JSON.parse(JSON.stringify(cfg)) : [];
      this.originalConfig = cfg && cfg.length > 0 ? JSON.parse(JSON.stringify(cfg)) : [];
      this.cdr.detectChanges();
    });
  }

  changeEntity(entity: string) {
    this.loadEntityConfig(entity);
  }

  onDragStart(i: number) {
    this.dragIndex = i;
  }

  onDrop(i: number) {
    if (this.dragIndex >= 0 && i !== this.dragIndex) {
      const arr = [...this.columnConfig];
      const [moved] = arr.splice(this.dragIndex, 1);
      arr.splice(i, 0, moved);
      this.columnConfig = arr;
      this.cdr.detectChanges();
    }
    this.dragIndex = -1;
  }

  updateField(i: number, key: keyof ColumnConfig, value: any) {
    // Create a new array to avoid mutating frozen store objects
    this.columnConfig = [...this.columnConfig];
    this.columnConfig[i] = { ...this.columnConfig[i], [key]: value };
    this.cdr.detectChanges();
  }

  saveConfig() {
    // Save to localStorage/backend - effect will update store automatically
    this.isSaving = true;
    this.cdr.detectChanges();
    this.store.dispatch(saveDefaultColumnConfig({ gridId: this.selectedEntity, config: this.columnConfig }));
  }

  resetConfig() {
    if (confirm('Reset to default columns for this entity?')) {
      this.store.dispatch(resetColumnConfig({ entity: this.selectedEntity }));
      this.loadEntityConfig(this.selectedEntity);
    }
  }

  trackByField(index: number, item: ColumnConfig) {
    return item.field;
  }
}
